import analytics from '@/shared-utils/analytics'
import { ActionCard } from '@pcln/cards'
import { Carousel } from '@pcln/horizon'
import { useEffect, useMemo } from 'react'
import { fireRecentSearchLoadEvent } from './ga4'
import type { RecentTripSearches } from './types'
import { getRecentSearchDisplayProps, onCardClick } from './utils'

export default function RecentSearchCarousel({
  pageName,
  items,
}: Readonly<{
  pageName: 'homepage' | 'landing'
  items: ReadonlyArray<RecentTripSearches>
}>) {
  useEffect(() => {
    if (items.length > 0) {
      fireRecentSearchLoadEvent(items, pageName)
    }
  }, [items, pageName])

  const slides = useMemo(
    () =>
      items.reduce<{ id: string; childrenNodes: React.ReactNode }[]>((validSlides, item, index) => {
        try {
          const { overline, heading, icon, actionText, relativeUrl } = getRecentSearchDisplayProps(item)
          return [
            ...validSlides,
            {
              id: `recent-search-${index}-${overline}`,
              childrenNodes: (
                <ActionCard
                  headingWidth={['194px', '100px', 'null', '190px', '200px', '250px']}
                  icon={icon}
                  heading={heading}
                  actionText={actionText}
                  overline={overline}
                  onClick={() => {
                    onCardClick(item, relativeUrl, pageName, item.productType)
                  }}
                  handleSubmitCTA={() => {
                    onCardClick(item, relativeUrl, pageName, item.productType)
                  }}
                  className='full-width'
                />
              ),
            },
          ]
        } catch (err) {
          analytics.logError({
            message: 'Error in getRecentSearchDisplayProps',
            errorMessage: err instanceof Error ? err.message : String(err),
            logObject: {
              error: JSON.stringify(err),
              item,
            },
          })
          return validSlides
        }
      }, []),
    [items, pageName],
  )

  return (
    <Carousel
      ariaLabel='Your recent searches'
      dragFree
      numSlides='one-two-three'
      slides={slides}
      slideWidth='same'
      pagination='numbers'
      type='content'
    />
  )
}
